import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ParentShare, Student } from '@/types';
import { formatDateTime, formatRelativeTime } from '@/lib/datetime';

interface ParentShareSectionProps {
  student: Student;
}

function buildShareUrl(token: string): string {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  return `${window.location.origin}${pathPrefix}/share/${token}`;
}

export default function ParentShareSection({ student }: ParentShareSectionProps) {
  const [share, setShare] = useState<ParentShare | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingAction, setPendingAction] = useState<'reissue' | 'stop' | null>(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('parent_shares')
        .select('*')
        .eq('student_id', student.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (fetchError) throw fetchError;
      setShare((data as ParentShare) ?? null);
    } catch {
      setError('공유 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshShare = async () => {
    setRefreshing(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('parent_shares')
        .select('*')
        .eq('student_id', student.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (fetchError) throw fetchError;
      setShare((data as ParentShare) ?? null);
    } catch {
      setError('열람 현황을 새로고침하지 못했어요.');
    } finally {
      setRefreshing(false);
    }
  };

  const createShare = async () => {
    setBusy(true);
    setError('');
    try {
      const token = crypto.randomUUID();
      const { data, error: insertError } = await supabase
        .from('parent_shares')
        .insert({ student_id: student.id, token, label: '학부모 공유' })
        .select()
        .maybeSingle();
      if (insertError) throw insertError;
      setShare((data as ParentShare) ?? null);
    } catch {
      setError('공유 링크 생성에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };

  const replaceShare = async () => {
    setBusy(true);
    setError('');
    try {
      await supabase.from('parent_shares').delete().eq('student_id', student.id);
      const token = crypto.randomUUID();
      const { data, error: insertError } = await supabase
        .from('parent_shares')
        .insert({ student_id: student.id, token, label: '학부모 공유' })
        .select()
        .maybeSingle();
      if (insertError) throw insertError;
      setShare((data as ParentShare) ?? null);
      setPendingAction(null);
    } catch {
      setError('링크 재발급에 실패했어요.');
    } finally {
      setBusy(false);
    }
  };

  const stopShare = async () => {
    setBusy(true);
    setError('');
    try {
      const { error: deleteError } = await supabase
        .from('parent_shares')
        .delete()
        .eq('student_id', student.id);
      if (deleteError) throw deleteError;
      setShare(null);
      setPendingAction(null);
    } catch {
      setError('공유 중지에 실패했어요.');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('링크 복사에 실패했어요. 링크를 길게 눌러 복사해주세요.');
    }
  };

  const shareUrl = share ? buildShareUrl(share.token) : '';
  const viewed = !!share && ((share.view_count ?? 0) > 0 || !!share.last_viewed_at);

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-share-forward-line"></i>
        </span>
        <h2 className="text-base font-bold text-foreground-950">학부모 공유</h2>
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
        아래 공유 링크를 학부모님께 보내드리면, 로그인 없이 {student.name} 학생의 학생카드·희망 학과/진로·남은 진도·수업일지·수업 일정 변경·보충 수업 일정·학교기출 결과·시험 일정·교습비를 바로 확인할 수 있어요.
      </p>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin"></i>
          불러오는 중...
        </div>
      ) : !share ? (
        <div className="mt-5 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 py-10">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-share-forward-line text-xl"></i>
          </span>
          <p className="text-sm text-foreground-600">아직 공유 링크가 없어요.</p>
          <button
            type="button"
            onClick={createShare}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-add-line"></i>
            )}
            공유 링크 만들기
          </button>
        </div>
      ) : (
        <div className="mt-5">
          <p className="text-xs font-semibold text-foreground-700">공유 링크</p>
          <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              readOnly
              value={shareUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-xs text-foreground-700"
            />
            <button
              type="button"
              onClick={() => copyLink(shareUrl)}
              className="flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-xs font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
            >
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
              {copied ? '복사됨' : '링크 복사'}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              미리보기
            </a>
            <button
              type="button"
              onClick={() => setPendingAction('reissue')}
              className="flex items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              링크 재발급
            </button>
            <button
              type="button"
              onClick={() => setPendingAction('stop')}
              className="flex items-center gap-1.5 rounded-md border border-accent-200 px-3.5 py-2 text-xs font-semibold text-accent-700 transition hover:bg-accent-50 whitespace-nowrap"
            >
              <i className="ri-link-unlink-m"></i>
              공유 중지
            </button>
          </div>

          {pendingAction && (
            <div className="mt-3 rounded-lg border border-accent-200 bg-accent-50 p-3.5">
              <p className="text-xs font-medium text-accent-900">
                {pendingAction === 'reissue'
                  ? '링크를 재발급하면 기존 링크는 즉시 사용할 수 없게 돼요. 계속할까요?'
                  : '공유를 중지하면 학부모님이 더 이상 이 링크로 접속할 수 없어요. 계속할까요?'}
              </p>
              <div className="mt-2.5 flex gap-2">
                <button
                  type="button"
                  onClick={pendingAction === 'reissue' ? replaceShare : stopShare}
                  disabled={busy}
                  className="flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
                >
                  {busy && <i className="ri-loader-4-line animate-spin"></i>}
                  확인
                </button>
                <button
                  type="button"
                  onClick={() => setPendingAction(null)}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
                >
                  취소
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-3.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-700">
                <i className="ri-eye-line text-secondary-600"></i>
                학부모 열람 현황
              </p>
              <button
                type="button"
                onClick={refreshShare}
                disabled={refreshing}
                className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-semibold text-foreground-600 transition hover:bg-background-100 disabled:opacity-50 whitespace-nowrap"
              >
                <i className={refreshing ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}></i>
                새로고침
              </button>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              <div className="rounded-md bg-background-50 px-3 py-2.5">
                <p className="text-[11px] text-foreground-500">열람 여부</p>
                {viewed ? (
                  <p className="mt-1 flex items-center gap-1 text-sm font-bold text-primary-700">
                    <i className="ri-checkbox-circle-line"></i>
                    열람함
                  </p>
                ) : (
                  <p className="mt-1 flex items-center gap-1 text-sm font-bold text-foreground-500">
                    <i className="ri-time-line"></i>
                    아직 열람 전
                  </p>
                )}
              </div>
              <div className="rounded-md bg-background-50 px-3 py-2.5">
                <p className="text-[11px] text-foreground-500">마지막 확인</p>
                {share.last_viewed_at ? (
                  <>
                    <p className="mt-1 text-sm font-bold text-foreground-900">
                      {formatRelativeTime(share.last_viewed_at)}
                    </p>
                    <p className="mt-0.5 text-[10px] text-foreground-400">
                      {formatDateTime(share.last_viewed_at)}
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-sm font-bold text-foreground-400">-</p>
                )}
              </div>
              <div className="rounded-md bg-background-50 px-3 py-2.5">
                <p className="text-[11px] text-foreground-500">조회 횟수</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">
                  {share.view_count ?? 0}
                  <span className="ml-0.5 text-xs font-medium text-foreground-500">회</span>
                </p>
              </div>
            </div>

            <p className="mt-2 flex items-center gap-1 text-[11px] text-foreground-400">
              <i className="ri-information-line"></i>
              학부모님이 링크를 열 때마다 자동으로 기록돼요.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}